import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

/**
 * SearchableSelect - Universal searchable dropdown component
 * 
 * Features:
 * - Real-time search/filter
 * - Keyboard navigation (↑ ↓ Enter Esc Tab)
 * - Highlight matched text
 * - Async data loading with debounce
 * - Custom option rendering
 * - Accessibility (ARIA roles, screen reader support)
 * - Clear selection option
 * - Multiple selection mode (optional)
 */

// Utility to highlight matched text
const HighlightMatch = ({ text, query }) => {
  if (!query || !text) return <span>{text}</span>;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = String(text).split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="search-highlight">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const SearchableSelect = ({
  // Data
  options = [],
  value,
  onChange,

  // Async loading
  onSearch,
  loading = false,
  debounceMs = 300,

  // Display
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  noResultsText = 'No results found',
  loadingText = 'Loading...',

  // Option rendering
  getOptionLabel = (opt) => opt?.label || opt?.name || String(opt),
  getOptionValue = (opt) => opt?.value || opt?.id || opt,
  getOptionIcon = null,
  getOptionDescription = null,
  renderOption = null,

  // Behavior
  clearable = true,
  searchable = true,
  disabled = false,
  required = false,
  autoFocus = false,
  closeOnSelect = true,

  // Multiple selection
  multiple = false,

  // Grouping
  groupBy = null,

  // Styling
  className = '',
  size = 'md', // 'sm', 'md', 'lg'
  variant = 'default', // 'default', 'inline', 'minimal'
  maxDropdownHeight = 280,
  dropdownWidth = null,
  preferBelow = false,

  // Callbacks
  onOpen,
  onClose,
  onFocus,
  onBlur,

  // Accessibility
  id,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [internalLoading, setInternalLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef([]);

  const debouncedQuery = useDebounce(searchQuery, debounceMs);

  // Filtered options (client-side filtering)
  const filteredOptions = useMemo(() => {
    if (onSearch) return options; // Let parent handle filtering for async
    if (!searchQuery) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(opt => {
      const label = String(getOptionLabel(opt)).toLowerCase();
      const description = getOptionDescription ? String(getOptionDescription(opt) || '').toLowerCase() : '';
      return label.includes(query) || description.includes(query);
    });
  }, [options, searchQuery, onSearch, getOptionLabel, getOptionDescription]);

  // Grouped options
  const groupedOptions = useMemo(() => {
    if (!groupBy) return null;

    const groups = {};
    filteredOptions.forEach(opt => {
      const group = groupBy(opt) || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    });
    return groups;
  }, [filteredOptions, groupBy]);

  // Flat list for keyboard navigation
  const flatOptions = useMemo(() => {
    if (groupedOptions) {
      return Object.values(groupedOptions).flat();
    }
    return filteredOptions;
  }, [filteredOptions, groupedOptions]);

  // Get selected option(s)
  const selectedOption = useMemo(() => {
    if (multiple) {
      return options.filter(opt =>
        Array.isArray(value) && value.includes(getOptionValue(opt))
      );
    }
    return options.find(opt => getOptionValue(opt) === value) || null;
  }, [options, value, multiple, getOptionValue]);

  // Async search effect
  useEffect(() => {
    if (onSearch && isOpen) {
      setInternalLoading(true);
      onSearch(debouncedQuery).finally(() => {
        setInternalLoading(false);
      });
    }
  }, [debouncedQuery, onSearch, isOpen]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(maxDropdownHeight + 60, 350);

    // Calculate width - use trigger width but with minimum
    const minWidth = 220;
    const calculatedWidth = Math.max(rect.width, minWidth);

    // Check if dropdown would overflow right side of viewport
    // Use very large safety margin (50px) for side panels where space is constrained
    const safetyMargin = 50;
    let leftPos = rect.left;
    if (leftPos + calculatedWidth > viewportWidth - safetyMargin) {
      leftPos = Math.max(safetyMargin, viewportWidth - calculatedWidth - safetyMargin);
    }

    // Determine if dropdown should open above or below
    const openAbove = !preferBelow && spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: openAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: leftPos,
      width: calculatedWidth,
      openAbove
    });
  }, [maxDropdownHeight, preferBelow]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    onClose?.();
  }, [onClose]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setSearchQuery('');
    setHighlightedIndex(-1);
    onOpen?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled, onOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isInsideContainer = containerRef.current?.contains(e.target);
      const isInsideDropdown = dropdownRef.current?.contains(e.target);

      if (!isInsideContainer && !isInsideDropdown) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    // Use requestAnimationFrame to ensure DOM is painted and any parent
    // animations (like modal slide-in) have started before measuring position
    let rafId = requestAnimationFrame(() => {
      // Double RAF to ensure layout is complete after animations start
      rafId = requestAnimationFrame(() => {
        calculatePosition();
      });
    });

    const handlePositionUpdate = () => {
      calculatePosition();
    };

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, calculatePosition]);

  const handleSelect = useCallback((option) => {
    if (multiple) {
      const optValue = getOptionValue(option);
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optValue)
        ? currentValues.filter(v => v !== optValue)
        : [...currentValues, optValue];
      onChange(newValues);
    } else {
      onChange(getOptionValue(option));
      if (closeOnSelect) handleClose();
    }
  }, [value, onChange, multiple, getOptionValue, closeOnSelect, handleClose]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange(multiple ? [] : null);
    handleClose();
  }, [onChange, multiple, handleClose]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          handleOpen();
        } else {
          setHighlightedIndex(prev =>
            prev < flatOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : flatOptions.length - 1
          );
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && flatOptions[highlightedIndex]) {
          handleSelect(flatOptions[highlightedIndex]);
        } else if (!isOpen) {
          handleOpen();
        }
        break;

      case 'Escape':
        e.preventDefault();
        handleClose();
        break;

      case 'Tab':
        if (isOpen) {
          handleClose();
        }
        break;

      case 'Backspace':
        if (!searchQuery && multiple && Array.isArray(value) && value.length > 0) {
          // Remove last selected item
          const newValues = value.slice(0, -1);
          onChange(newValues);
        }
        break;

      default:
        if (!isOpen && e.key.length === 1) {
          handleOpen();
        }
        break;
    }
  }, [disabled, isOpen, highlightedIndex, flatOptions, handleOpen, handleClose, handleSelect, searchQuery, multiple, value, onChange]);

  // Render selected value(s)
  const renderSelectedValue = () => {
    if (multiple && Array.isArray(value) && value.length > 0) {
      return (
        <div className="selected-tags">
          {selectedOption.map((opt, idx) => (
            <span key={idx} className="selected-tag">
              {getOptionIcon && (
                <span className="tag-icon">{getOptionIcon(opt)}</span>
              )}
              <span className="tag-label">{getOptionLabel(opt)}</span>
              <button
                type="button"
                className="tag-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(opt);
                }}
                aria-label={`Remove ${getOptionLabel(opt)}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      );
    }

    if (selectedOption && !multiple) {
      return (
        <div className="selected-value">
          {getOptionIcon && (
            <span className="selected-icon">{getOptionIcon(selectedOption)}</span>
          )}
          <span className="selected-label">{getOptionLabel(selectedOption)}</span>
        </div>
      );
    }

    return <span className="placeholder-text">{placeholder}</span>;
  };

  // Render single option
  const renderOptionItem = (option, index, isSelected) => {
    const isHighlighted = index === highlightedIndex;

    if (renderOption) {
      return renderOption({
        option,
        isSelected,
        isHighlighted,
        searchQuery,
        getOptionLabel,
        getOptionValue,
      });
    }

    return (
      <>
        {getOptionIcon && (
          <span className="option-icon">{getOptionIcon(option)}</span>
        )}
        <div className="option-content">
          <span className="option-label">
            <HighlightMatch text={getOptionLabel(option)} query={searchQuery} />
          </span>
          {getOptionDescription && (
            <span className="option-description">
              <HighlightMatch text={getOptionDescription(option)} query={searchQuery} />
            </span>
          )}
        </div>
        {isSelected && (
          <span className="option-check" aria-hidden="true">✓</span>
        )}
      </>
    );
  };

  const isLoading = loading || internalLoading;
  const hasValue = multiple ? (Array.isArray(value) && value.length > 0) : (value != null && value !== '');

  return (
    <div
      ref={containerRef}
      className={`searchable-select ${className} size-${size} variant-${variant} ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${hasValue ? 'has-value' : ''}`}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <div
        ref={triggerRef}
        className="select-trigger"
        onClick={() => isOpen ? handleClose() : handleOpen()}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${id || 'select'}-listbox`}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {renderSelectedValue()}

        <div className="select-actions">
          {clearable && hasValue && !disabled && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClear}
              aria-label="Clear selection"
              tabIndex={-1}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          <span className={`dropdown-arrow ${isOpen ? 'is-open' : ''}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      </div>

      {/* Dropdown - Rendered via Portal to prevent clipping */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`select-dropdown ${dropdownPosition.openAbove ? 'open-above' : ''}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownWidth || dropdownPosition.width,
            maxHeight: maxDropdownHeight,
            zIndex: 10001 // Above modal overlays (typically 1000)
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="search-input-wrapper">
              <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="16" cy="11" r="6" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus={autoFocus}
                aria-label="Search options"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Options List */}
          <div
            ref={listRef}
            className="options-list"
            role="listbox"
            id={`${id || 'select'}-listbox`}
            aria-multiselectable={multiple}
          >
            {isLoading ? (
              <div className="loading-state">
                <span className="loading-spinner" />
                <span>{loadingText}</span>
              </div>
            ) : flatOptions.length === 0 ? (
              <div className="empty-state">
                {noResultsText}
              </div>
            ) : groupedOptions ? (
              // Grouped rendering
              Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
                <div key={groupName} className="option-group">
                  <div className="group-header">{groupName}</div>
                  {groupOptions.map((option, idx) => {
                    const globalIndex = flatOptions.indexOf(option);
                    const optValue = getOptionValue(option);
                    const isSelected = multiple
                      ? Array.isArray(value) && value.includes(optValue)
                      : value === optValue;

                    return (
                      <div
                        key={optValue}
                        ref={(el) => optionRefs.current[globalIndex] = el}
                        className={`option-item ${isSelected ? 'is-selected' : ''} ${globalIndex === highlightedIndex ? 'is-highlighted' : ''}`}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelect(option)}
                        onMouseEnter={() => setHighlightedIndex(globalIndex)}
                      >
                        {renderOptionItem(option, globalIndex, isSelected)}
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              // Flat rendering
              flatOptions.map((option, index) => {
                const optValue = getOptionValue(option);
                const isSelected = multiple
                  ? Array.isArray(value) && value.includes(optValue)
                  : value === optValue;

                return (
                  <div
                    key={optValue}
                    ref={(el) => optionRefs.current[index] = el}
                    className={`option-item ${isSelected ? 'is-selected' : ''} ${index === highlightedIndex ? 'is-highlighted' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {renderOptionItem(option, index, isSelected)}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body // Render dropdown to body to prevent clipping
      )}

      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={multiple ? JSON.stringify(value || []) : (value || '')}
        />
      )}
    </div>
  );
};

export default SearchableSelect;

// Export utility components
export { HighlightMatch, useDebounce };
